import format from 'date-fns/format';

/**
 * Finds a shared single-line font size for all seven weekday labels.
 * Keeps the requested size when it fits; otherwise chooses the largest whole
 * size that fits both dimensions, down to the editor's minimum size of 1.
 * Measurement must use the loaded font family, weight and style of the header.
 *
 * @param {Object} options
 * @param {number} options.fontSize Requested maximum font size in calendar SVG units.
 * @param {number} options.width Available width after left and right padding.
 * @param {number} options.height Available height after top and bottom padding.
 * @param {string} options.dayFormat The same date-fns format used by WeekdayName.
 * @param {(text: string, fontSize: number) => number} options.measureText Text width in SVG units.
 * @returns {number} Applied font size, never larger than the requested size.
 */
export const fitWeekdayNames = ({ fontSize, width, height, dayFormat = 'EEEEEE', measureText }) => {
  const labels = Array.from({ length: 7 }, (_, index) => format(new Date(2024, 0, 7 + index), dayFormat));
  const fits = (size) => size <= height + 1e-6 && labels.every((label) => measureText(label, size) <= width + 1e-6);
  if (fits(fontSize)) return fontSize;

  let low = 1;
  let high = Math.floor(Math.min(fontSize, height));
  let fittedSize = Math.min(1, fontSize);
  while (low <= high) {
    const size = Math.floor((low + high) / 2);
    if (fits(size)) {
      fittedSize = size;
      low = size + 1;
    } else {
      high = size - 1;
    }
  }
  return fittedSize;
};
